import { MetadataFormData } from "@saleor/components/Metadata";
import { MultiAutocompleteChoiceType } from "@saleor/components/MultiAutocompleteSelectField";
import { SingleAutocompleteChoiceType } from "@saleor/components/SingleAutocompleteSelectField";
import useForm, { FormChange, SubmitPromise } from "@saleor/hooks/useForm";
import useFormset, {
  FormsetChange,
  FormsetData
} from "@saleor/hooks/useFormset";
import useNotifier from "@saleor/hooks/useNotifier";
import { ProductDetails_product } from "@saleor/products/types/ProductDetails";
import { ProductVariant_productVariant_stockOnMall } from "@saleor/products/types/ProductVariant";
import {
  getAttributeInputFromProduct,
  getProductUpdatePageFormData,
  getStockInputFromProduct
} from "@saleor/products/utils/data";
import {
  createAttributeChangeHandler,
  createAttributeMultiChangeHandler
} from "@saleor/products/utils/handlers";
import { SearchWarehouses_search_edges_node } from "@saleor/searches/types/SearchWarehouses";
import handleFormSubmit from "@saleor/utils/handlers/handleFormSubmit";
import createMultiAutocompleteSelectHandler from "@saleor/utils/handlers/multiAutocompleteSelectChangeHandler";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import { RawDraftContentState } from "draft-js";
import { diff } from "fast-array-diff";
import React from "react";
import { useIntl } from "react-intl";

import { ProductAttributeInput } from "../ProductAttributes";
import { ProductStockInput } from "../ProductStocks";

export interface ProductUpdateFormData extends MetadataFormData {
  availableForPurchase: string;
  basePrice: number;
  brand: string;
  brandLabel: string;
  category: string | null;
  changeTaxCode: boolean;
  chargeTaxes: boolean;
  collections: string[];
  description: RawDraftContentState;
  isAvailable: boolean;
  isAvailableForPurchase: boolean;
  isPublished: boolean;
  isPublishedByTenant: boolean;
  name: string;
  slug: string;
  productStatus: string;
  publicationDate: string;
  salePrice: string;
  seoDescription: string;
  seoTitle: string;
  sku: string;
  stockOnMall: string;
  tags: MultiAutocompleteChoiceType[];
  taxCode: string;
  trackInventory: boolean;
  visibleInListings: boolean;
  weight: string;
  width: string;
  height: string;
  length: string;
  nonCod: boolean;
}
export interface ProductUpdateData extends ProductUpdateFormData {
  attributes: ProductAttributeInput[];
  stocks: ProductStockInput[];
}
export interface ProductUpdateSubmitData extends ProductUpdateFormData {
  attributes: ProductAttributeInput[];
  collections: string[];
  addStocks: ProductStockInput[];
  updateStocks: ProductStockInput[];
  removeStocks: string[];
}

type ProductUpdateHandlers = Record<
  "changeMetadata" | "selectCategory" | "selectCollection" | "selectTaxRate",
  FormChange
> &
  Record<
    "changeStock" | "selectAttribute" | "selectAttributeMultiple",
    FormsetChange<string>
  > &
  Record<"addStock" | "deleteStock", (id: string) => void>;
export interface UseProductUpdateFormResult {
  change: FormChange;
  data: ProductUpdateData;
  handlers: ProductUpdateHandlers;
  hasChanged: boolean;
  submit: () => Promise<boolean>;
}

export interface UseProductUpdateFormOpts
  extends Record<
    "categories" | "collections" | "taxTypes",
    SingleAutocompleteChoiceType[]
  > {
  setSelectedCategory: React.Dispatch<React.SetStateAction<string>>;
  setSelectedCollections: React.Dispatch<
    React.SetStateAction<MultiAutocompleteChoiceType[]>
  >;
  setSelectedTaxType: React.Dispatch<React.SetStateAction<string>>;
  selectedCollections: MultiAutocompleteChoiceType[];
  warehouses: SearchWarehouses_search_edges_node[];
  useMallVisibility?: boolean;
}

export interface ProductUpdateFormProps extends UseProductUpdateFormOpts {
  children: (props: UseProductUpdateFormResult) => React.ReactNode;
  product: ProductDetails_product;
  stockOnMall: ProductVariant_productVariant_stockOnMall;
  onSubmit: (data: ProductUpdateSubmitData) => SubmitPromise;
}

const getAvailabilityData = ({
  availableForPurchase,
  isAvailableForPurchase,
  isPublished
}: ProductUpdateFormData) => ({
  isAvailableForPurchase: isAvailableForPurchase || !!availableForPurchase,
  isPublished
});

const getStocksData = (
  product: ProductDetails_product,
  stocks: FormsetData<null, string>
) => {
  if (product?.productType?.hasVariants) {
    return { addStocks: [], removeStocks: [], updateStocks: [] };
  }

  const dataStocks = stocks.map(stock => stock.id);
  const variantStocks =
    product?.variants[0]?.stocks.map(stock => stock.warehouse.id) || [];
  const stockDiff = diff(variantStocks, dataStocks);

  return {
    addStocks: stocks.filter(stock =>
      stockDiff.added.some(addedStock => addedStock === stock.id)
    ),
    removeStocks: stockDiff.removed,
    updateStocks: stocks.filter(
      stock => !stockDiff.added.some(addedStock => addedStock === stock.id)
    )
  };
};

const getMetadata = (
  data: ProductUpdateFormData,
  isMetadataModified: boolean,
  isPrivateMetadataModified: boolean
) => ({
  metadata: isMetadataModified ? data.metadata : undefined,
  privateMetadata: isPrivateMetadataModified ? data.privateMetadata : undefined
});

function useProductUpdateForm(
  product: ProductDetails_product,
  stockOnMall: ProductVariant_productVariant_stockOnMall,
  onSubmit: (data: ProductUpdateSubmitData) => SubmitPromise,
  opts: UseProductUpdateFormOpts
): UseProductUpdateFormResult {
  const intl = useIntl();
  const notify = useNotifier();
  const [changed, setChanged] = React.useState(false);
  const triggerChange = () => setChanged(true);

  const form = useForm(
    getProductUpdatePageFormData(product, stockOnMall, product?.variants, {
      useMallVisibility: opts.useMallVisibility
    })
  );
  const attributes = useFormset(getAttributeInputFromProduct(product));
  const stocks = useFormset(getStockInputFromProduct(product));
  const isPublishedRef = React.useRef(form.data.isPublished);
  isPublishedRef.current = form.data.isPublished;

  const {
    isMetadataModified,
    isPrivateMetadataModified,
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();

  const notifyUnavailableWithoutPublish = () => {
    notify({
      status: "error",
      text: intl.formatMessage({
        defaultMessage: "Product cannot be available if not published",
        description: "snackbar text"
      })
    });
  };

  const handleChange: FormChange = (event, cb) => {
    const { name, value } = event.target;

    // Keep availability in sync with publish state (instead of correcting in render).
    if (name === "isPublished") {
      isPublishedRef.current = value;
      form.change(event, cb);
      if (value === false && form.data.isAvailableForPurchase) {
        form.change({
          target: {
            name: "isAvailableForPurchase",
            value: false
          }
        });
        notifyUnavailableWithoutPublish();
      }
      triggerChange();
      return;
    }

    if (
      name === "isAvailableForPurchase" &&
      value === true &&
      !isPublishedRef.current
    ) {
      notifyUnavailableWithoutPublish();
      return;
    }

    form.change(event, cb);
    triggerChange();
  };
  const handleCollectionSelect = createMultiAutocompleteSelectHandler(
    form.toggleValue,
    opts.setSelectedCollections,
    opts.selectedCollections,
    opts.collections
  );
  const handleCategorySelect = createSingleAutocompleteSelectHandler(
    handleChange,
    opts.setSelectedCategory,
    opts.categories
  );
  const handleAttributeChange = createAttributeChangeHandler(
    attributes.change,
    triggerChange
  );
  const handleAttributeMultiChange = createAttributeMultiChangeHandler(
    attributes.change,
    attributes.data,
    triggerChange
  );
  const handleStockChange: FormsetChange<string> = (id, value) => {
    triggerChange();
    stocks.change(id, value);
  };
  const handleStockAdd = (id: string) => {
    triggerChange();
    stocks.add({
      data: null,
      id,
      label: opts.warehouses.find(warehouse => warehouse.id === id).name,
      value: "0"
    });
  };
  const handleStockDelete = (id: string) => {
    triggerChange();
    stocks.remove(id);
  };
  const handleTaxTypeSelect = createSingleAutocompleteSelectHandler(
    handleChange,
    opts.setSelectedTaxType,
    opts.taxTypes
  );
  const changeMetadata = makeMetadataChangeHandler(handleChange);

  const data: ProductUpdateData = {
    ...form.data,
    attributes: attributes.data,
    stocks: stocks.data
  };

  const submitData: ProductUpdateSubmitData = {
    ...data,
    ...getAvailabilityData(data),
    ...getStocksData(product, stocks.data),
    ...getMetadata(data, isMetadataModified, isPrivateMetadataModified),
    addStocks: [],
    attributes: attributes.data
  };

  const submit = () => handleFormSubmit(submitData, onSubmit, setChanged);

  return {
    change: handleChange,
    data,
    handlers: {
      addStock: handleStockAdd,
      changeMetadata,
      changeStock: handleStockChange,
      deleteStock: handleStockDelete,
      selectAttribute: handleAttributeChange,
      selectAttributeMultiple: handleAttributeMultiChange,
      selectCategory: handleCategorySelect,
      selectCollection: handleCollectionSelect,
      selectTaxRate: handleTaxTypeSelect
    },
    hasChanged: changed,
    submit
  };
}

const ProductUpdateForm: React.FC<ProductUpdateFormProps> = ({
  children,
  product,
  stockOnMall,
  onSubmit,
  ...rest
}) => {
  const props = useProductUpdateForm(product, stockOnMall, onSubmit, rest);

  return <form onSubmit={props.submit}>{children(props)}</form>;
};

ProductUpdateForm.displayName = "ProductUpdateForm";
export default ProductUpdateForm;
